/**
 * The handle the rest of the app uses to borrow the live viewport.
 *
 * This lives apart from `Canvas.tsx` for a concrete reason: React Fast Refresh
 * only preserves a module's state when that module exports components and
 * nothing else. Exporting `captureProxy` alongside the component invalidated
 * the whole module on every edit, and a half-applied refresh left the viewport
 * throwing on identifiers that were perfectly well defined in the source. The
 * split keeps `Canvas.tsx` a pure component module.
 */

import { defaultRecipe, type DecodedImage, type Recipe } from '@lumos/core';
import type { LumosRenderer, RenderOptions, ViewState } from '../engine/renderer.ts';

/** Long edge of the preview handed to the planner and measured for statistics. */
export const PROXY_EDGE = 1024;
export const PROXY_QUALITY = 0.86;

/** Inspection overlays must never reach the statistics or the planner. */
export const CLEAN_OPTIONS: RenderOptions = {
  showClipping: false,
  overlayMask: -1,
  overlayStrength: 0,
  splitPosition: null,
};

export interface Frame {
  data: Uint8ClampedArray<ArrayBuffer>;
  width: number;
  height: number;
}

export interface Live {
  photoId: string | null;
  image: DecodedImage | null;
  recipe: Recipe | null;
  options: RenderOptions;
  view: ViewState;
}

export interface Host {
  renderer: LumosRenderer;
  live: () => Live;
  redraw: () => void;
  /** Uploads the selected image if this renderer has not received it yet. */
  ensure: () => void;
}

export const EMPTY_LIVE: Live = {
  photoId: null,
  image: null,
  recipe: null,
  options: CLEAN_OPTIONS,
  view: { zoom: 1, panX: 0, panY: 0, mode: 'fit' },
};

let host: Host | null = null;
let cachedProxy: { photoId: string; recipe: Recipe; dataUrl: string } | null = null;

export function registerHost(next: Host): void {
  host = next;
}

export function clearHost(current: Host): void {
  // Only the mounted viewport may stand down; a late unmount from a replaced
  // canvas must not unregister the one that took its place.
  if (host === current) host = null;
  cachedProxy = null;
}

export function cacheProxy(photoId: string, recipe: Recipe, dataUrl: string): void {
  cachedProxy = { photoId, recipe, dataUrl };
}

/** The recipe stripped back to neutral, minus the framing, which is not an edit to undo. */
export function neutralWithGeometry(recipe: Recipe): Recipe {
  const neutral = defaultRecipe();
  neutral.crop = recipe.crop ? { ...recipe.crop } : null;
  neutral.rotate = recipe.rotate;
  neutral.orientation = recipe.orientation;
  neutral.flipHorizontal = recipe.flipHorizontal;
  neutral.flipVertical = recipe.flipVertical;
  return neutral;
}

export function geometryKey(recipe: Recipe): string {
  const crop = recipe.crop;
  return [
    crop ? `${crop.x},${crop.y},${crop.width},${crop.height}` : 'full',
    recipe.rotate,
    recipe.orientation,
    recipe.flipHorizontal ? 'h' : '-',
    recipe.flipVertical ? 'v' : '-',
  ].join('|');
}

export function toImageData(frame: Frame): ImageData {
  return new ImageData(frame.data, frame.width, frame.height);
}

/** Downscales a read-back frame onto a 2D canvas, which is what both JPEG and stats want. */
export function proxyCanvas(frame: Frame, maxEdge: number): HTMLCanvasElement {
  const scale = Math.min(1, maxEdge / Math.max(frame.width, frame.height));
  const full = document.createElement('canvas');
  full.width = frame.width;
  full.height = frame.height;
  const fullContext = full.getContext('2d', { willReadFrequently: true });
  if (!fullContext) throw new Error('The browser refused a 2D canvas for the preview.');
  fullContext.putImageData(toImageData(frame), 0, 0);
  if (scale === 1) return full;

  const small = document.createElement('canvas');
  small.width = Math.max(1, Math.round(frame.width * scale));
  small.height = Math.max(1, Math.round(frame.height * scale));
  const context = small.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('The browser refused a 2D canvas for the preview.');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(full, 0, 0, small.width, small.height);
  return small;
}

/**
 * The current frame and the same frame with every edit removed, as JPEG data
 * URLs, for the assistant to look at.
 *
 * The neutral render is read back and the live render restored inside one
 * synchronous block: nothing can be painted, and no other caller can interleave,
 * while the canvas is holding the unedited frame.
 */
export async function captureProxy(): Promise<{ current: string; original: string }> {
  const active = host;
  const live = active?.live();
  if (!active || !live?.image || !live.recipe || !live.photoId) {
    throw new Error('Open a photograph before asking about it.');
  }

  active.ensure();

  const cached =
    cachedProxy && cachedProxy.photoId === live.photoId && cachedProxy.recipe === live.recipe
      ? cachedProxy.dataUrl
      : null;

  const overlaid = live.options.showClipping || live.options.overlayMask >= 0;
  let currentFrame: Frame | null = null;
  if (!cached) {
    if (overlaid) active.renderer.process(live.recipe, live.image, CLEAN_OPTIONS);
    currentFrame = active.renderer.readProcessed();
  }
  active.renderer.process(neutralWithGeometry(live.recipe), live.image, CLEAN_OPTIONS);
  const originalFrame = active.renderer.readProcessed();
  active.redraw();

  if (!originalFrame || (!cached && !currentFrame)) {
    throw new Error('The renderer could not read the frame back.');
  }

  // Encoding is slow enough to be worth yielding for; the canvas is already correct.
  await new Promise((resolve) => window.setTimeout(resolve, 0));
  const current =
    cached ?? proxyCanvas(currentFrame as Frame, PROXY_EDGE).toDataURL('image/jpeg', PROXY_QUALITY);
  if (!cached) cacheProxy(live.photoId, live.recipe, current);

  await new Promise((resolve) => window.setTimeout(resolve, 0));
  const original = proxyCanvas(originalFrame, PROXY_EDGE).toDataURL('image/jpeg', PROXY_QUALITY);

  return { current, original };
}
